import {
    CompanyConfig,
    CompanyConfigResourceApi,
    CompanyDTO,
    CompanyResourceApi
} from '@root/api';
import { doPost, translateAxiosError, TranslatedAxiosError } from '../services/migrated/axiosWrapper';
import { errorService } from './newError.service';
import { authService } from './auth.service';
import { TranslateFn } from '../utils/translate_fn.types';

type CompanyCallback<T = unknown> = (err: TranslatedAxiosError | null, result?: T) => void;

const companyApi = new CompanyResourceApi();
const companyConfigApi = new CompanyConfigResourceApi();

const INDUSTRY_TYPE_IDS = [
    'ACCOMMODATION_AND_FOOT_SERVICES',
    'ARTS_ENTERTAINMENT_RECREATION',
    'COMPUTER_SOFTWARE_IT_SERVICES',
    'CONSTRUCTION',
    'EDUCATIONAL_SERVICES',
    'FINANCIAL_SERVICES_INSURANCE',
    'HEALTH_CARE_SOCIAL_ASSISTANCE',
    'MANAGEMENT_CONSULTING',
    'MANUFACTURING',
    'MARKETING_ADVERTISING',
    'NATURAL_RESOURCES',
    'NON_PROFIT_ORGANIZATION',
    'PROFESSIONAL_SCIENTIFIC_AND_TECHNICAL_SERVICES',
    'PUBLIC_AND_GOVERMENT_ADMINISTRATION',
    'REAL_ESTATE_RENTAL_AND_LEASING',
    'TRANSPORTATION_AND_WAREHOUSING',
    'UTILITIES_AND_SERVICES',
    'WHOLESALE_AND_RETAIL_TRADE'
] as const;

export const getIndustryTypesList = (translate: TranslateFn) =>
    INDUSTRY_TYPE_IDS.map((id) => ({
        id,
        label: translate(id)
    }));

export const jobEnvironmentTypesList = [
    { id: 'OFFICE', label: 'Office' },
    { id: 'NON_DESKTOP', label: 'Non Desktop' },
    { id: 'GEEK', label: 'Geek people' }
];

export const resetUserCompanyCache = (): void => {
    // OpenAPI clients do not use Angular $cacheFactory; kept for API compatibility.
};

export const getUserCompany = (callback: CompanyCallback<CompanyDTO>): void => {
    companyApi.getUserCompany()
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const getCurrentCompanyConfig = (callback: CompanyCallback<CompanyConfig>): void => {
    companyConfigApi.getCompanyCurrentCompanyConfig()
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const getConfigKey = (key: string, callback: CompanyCallback<unknown>): void => {
    getCurrentCompanyConfig((err, companyConfig) => {
        if (err || !companyConfig) {
            callback(err);
            return;
        }
        if (Object.prototype.hasOwnProperty.call(companyConfig, key)) {
            callback(null, (companyConfig as Record<string, unknown>)[key]);
            return;
        }
        callback({
            status: null,
            data: { err: 'NO_CONFIG_FOUND', msg: `Key ${key} not found!` }
        });
    });
};

export const getCompanyIndustryName = (callback: CompanyCallback<string | null>): void => {
    getCurrentCompanyConfig((err, companyConfig) => {
        if (err || !companyConfig) {
            callback(err);
            return;
        }
        callback(null, companyConfig.industryType ?? null);
    });
};

export const changeCompany = (companyId: string, success: (config: CompanyConfig) => void): void => {
    companyApi.changeCompany(companyId)
        .then((changeResponse) => {
            authService.storeCompanyUser(changeResponse.data);
            companyConfigApi.getCompanyCurrentCompanyConfig()
                .then((response) => {
                    resetUserCompanyCache();
                    success(response.data);
                })
                .catch((error) => errorService.reportError(error));
        })
        .catch((error) => errorService.reportError(error));
};

export const deleteCompany = (companyId: string, secret: string, callback: CompanyCallback): void => {
    companyApi.deleteCompany(companyId, secret)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const resetCompany = (companyId: string, secret: string, callback: CompanyCallback): void => {
    companyApi.resetCompany(companyId, secret)
        .then((response) => {
            resetUserCompanyCache();
            callback(null, response.data);
        })
        .catch((error) => callback(translateAxiosError(error)));
};

export const isNotEnoughActiveEmployees = (error: unknown): boolean =>
    errorService.isNotEnoughActiveEmployees(error as Parameters<typeof errorService.isNotEnoughActiveEmployees>[0]);

export const getAllCompanies = (
    callback: CompanyCallback,
    name?: string,
    from?: number,
    to?: number,
    signal?: AbortSignal
): void => {
    companyApi.getCompanies(name, from, to, signal ? { signal } : undefined)
        .then((response) => {
            if (signal?.aborted) {
                return;
            }
            callback(null, response.data);
        })
        .catch((error) => {
            if (signal?.aborted) {
                return;
            }
            callback(translateAxiosError(error));
        });
};

export const changeCompanyStatus = (newCompanyStatus: string, callback: CompanyCallback): void => {
    getUserCompany((err, company) => {
        if (err || !company?.id) {
            callback(err);
            return;
        }
        doPost(
            `/company/config/${company.id}/${newCompanyStatus}`,
            {},
            { companyId: company.id },
            callback
        );
    });
};

export const saveCompany = (companyDetails: CompanyDTO, callback: CompanyCallback<CompanyDTO>): void => {
    companyConfigApi.updateCompany(companyDetails)
        .then((response) => {
            resetUserCompanyCache();
            callback(null, response.data);
        })
        .catch((error) => callback(translateAxiosError(error)));
};

export const saveCompanyConfig = (
    companyId: string,
    companyConfig: CompanyConfig,
    callback: CompanyCallback<CompanyConfig>
): void => {
    companyConfigApi.updateCompanyConfig(companyId, companyConfig)
        .then((response) => {
            resetUserCompanyCache();
            callback(null, response.data);
        })
        .catch((error) => callback(translateAxiosError(error)));
};

export const companyService = {
    resetUserCompanyCache,
    getConfigKey,
    getUserCompany,
    getCurrentCompanyConfig,
    getCompanyIndustryName,
    changeCompany,
    deleteCompany,
    resetCompany,
    getAllCompanies,
    changeCompanyStatus,
    saveCompany,
    saveCompanyConfig,
    getIndustryTypesList,
    industryTypeIds: INDUSTRY_TYPE_IDS,
    jobEnvironmentTypesList,
    isNotEnoughActiveEmployees
};
